import React, { useCallback } from 'react';
import {
  Form,
  RecordContextProvider,
  required,
  useGetOne,
  useRefresh,
  useUpdate,
} from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import ArrowBackIosFilled from '@mui/icons-material/ArrowBackIos';
import conflictFieldsAnswerFields from './conflictFieldsAnswerField';
import { DatapointValue, DEFAULT_SX } from './ConflictValueListView';
import languages from '../../constants/languages';
import useConflictContext from './useConflictContext';
import CommentInput from '../comment/CommentInput';

const ConflictMarkAsVerified = () => {
  const { valueId } = useParams();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const navigate = useNavigate();

  const { searchParams } = useConflictContext();

  const handleClose = useCallback(() => {
    navigate(`..?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  const { data: datapointValue, isLoading } = useGetOne<DatapointValue>('data-points', { id: valueId ?? '' }, { enabled: !!valueId });

  const handleSubmit = useCallback((data: { comment?: string }) => {
    const { comment } = data;
    update(
      'data-points/select-one',
      { id: valueId, data: { comment } },
      {
        onSuccess: () => {
          handleClose();
          refresh();
        },
      },
    );
  }, [handleClose, valueId, refresh, update]);

  if (isLoading || !datapointValue) return null;

  return (
    <Grid container gap={1}>
      <Grid
        mb={3}
        size={12}
      >
        <Box display="flex" alignItems="center">
          <IconButton size="medium" onClick={handleClose}>
            <ArrowBackIosFilled fontSize="medium" color="primary" />
          </IconButton>
          <Typography color="primary" variant="h5">Back</Typography>
        </Box>
      </Grid>
      <Grid size={12}>
        <Grid container>
          <Grid
            sx={{ backgroundColor: 'background.paper' }}
            size={12}
          >
            <Card
              sx={{ m: 0 }}
              variant="outlined"
            >
              <CardContent>
                <Form record={{ comment: '' }} onSubmit={handleSubmit}>
                  <Grid container spacing={4}>
                    <Grid size={12}>
                      <Typography color="primary" variant="h5">Mark as verified</Typography>
                    </Grid>
                    <Grid size={12}>
                      <RecordContextProvider value={{ ...datapointValue, sx: DEFAULT_SX }}>
                        {React.createElement(
                          conflictFieldsAnswerFields[datapointValue.name],
                          { language: languages.EN },
                        )}
                      </RecordContextProvider>
                    </Grid>
                    <Grid size={12}>
                      <CommentInput
                        source="comment"
                        validate={required()}
                        placeholder="Please explain your decision."
                      />
                    </Grid>
                    <Grid
                      display="flex"
                      justifyContent="center"
                      size={12}
                    >
                      <Box display="flex" gap={8}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button variant="contained" type="submit">Save</Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ConflictMarkAsVerified;
